import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Columns3, X } from 'lucide-react';
import apiClient from '../../config/axios';
import ErrorBoundary from '../../components/ErrorBoundary';
import { resolvedPrinting } from '../../components/generic/PrintingsPicker';
import { getFamily } from '../../config/familyRegistry';
import { getActiveSystem } from '../../config/system';
import { attributeText } from '../../utils/formatEntry';
import { summarizeTrack } from '../../utils/trackSummary';

/**
 * The Compare view (issue 188): 2–4 entries of ONE Family, side by side.
 *
 * THE SPINE IS THE REGISTRY, NOT A CONFIG. The issue predates the
 * re-platforming and proposed "one small config per comparable type"; there
 * are no types to hang configs on any more — every Family's attributes,
 * labels, datatypes, units and display order are already authored in the
 * Family registry, and that is the spine. The rows are the Family's attribute
 * scan in display order, minus what a comparison table cannot hold:
 *
 *   • prose (`isProse` — the description a reader came for is a paragraph,
 *     not a comparable fact; the name links back to the full detail page),
 *   • structured tables (a class's level table does not fit a cell),
 *   • reverse sections (derived sets about OTHER entries — "required by" —
 *     not facts of this one).
 *
 * Everything else renders as a row, spelled by `attributeText` — the same
 * seam the detail renderer and the stat-block copy use — so a value cannot
 * read differently here than on the detail page, and a price stays in its
 * AUTHORED denomination. A row where no compared entry has a value is
 * omitted; a cell with no value reads "—". A Family a user authors this
 * afternoon is comparable by construction, through the same runtime registry
 * install every other generic page uses.
 *
 * SAME-FAMILY ONLY, STRUCTURALLY: the address is `/<segment>?compare=a,b,c`
 * — the entity stays the single path segment (the app's query-param-only URL
 * convention), so a mixed-Family comparison cannot even be written down.
 *
 * THE PRINTING PROBLEM (named by issue 202): a slug can be printed in several
 * books and some values differ between printings, so a comparison that
 * silently mixes printings is worse than useless. Each column resolves its
 * printing exactly as the detail route does — the canonical (earliest
 * published) printing unless pinned — and the column header ALWAYS labels
 * which printing is shown. A reader can pin a printing per column: the pin
 * rides in the URL as `slug@rulebook-slug`, so a shared link reproduces the
 * exact printings compared, the same one-mechanism rule the detail page's
 * `?rulebook=` pin follows.
 *
 * THE CAP IS IN THE PARSER: `parseCompareParam` truncates to MAX_COMPARE
 * before anything is fetched, so a URL naming 50 slugs costs 4 requests, not
 * 50, regardless of what any UI enforced.
 *
 * Mobile: the table scrolls inside its own `overflow-x-auto` container with
 * a sticky label column; the page body never scrolls horizontally (house
 * convention).
 */

export const MAX_COMPARE = 4;

/**
 * `?compare=` tokens: `slug` or `slug@rulebook-slug`, comma separated.
 * Deduped by slug, truncated to MAX_COMPARE — the request cap lives here.
 */
export function parseCompareParam(raw) {
  const seen = new Set();
  const out = [];
  for (const token of String(raw || '').split(',')) {
    const t = token.trim();
    if (!t) continue;
    const at = t.indexOf('@');
    const slug = at >= 0 ? t.slice(0, at) : t;
    const rulebook = at >= 0 ? t.slice(at + 1) : null;
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    if (out.length < MAX_COMPARE) out.push({ slug, rulebook: rulebook || null });
  }
  return { tokens: out, truncated: seen.size > out.length };
}

const tokenString = (t) => (t.rulebook ? `${t.slug}@${t.rulebook}` : t.slug);

/** The comparison rows this Family's registry entry declares. */
export function compareSpine(family) {
  return family.attributes.filter((a) => a.register !== 'name'
    && !a.isProse
    && a.datatype !== 'structured'
    && a.kind !== 'reverse'
    && (a.showInDetail || a.inDetailHeader));
}

/**
 * The structured attributes a compare row MAY come from (issue 628): a
 * per-level track — a class's attack or save progression, its spells-per-day
 * grid — summarised to one line by `summarizeTrack`. Whether a row appears is
 * decided by the values (a structured value that is not a track summarises
 * to null and earns no row), so this is every structured attribute, detail
 * visibility aside: the tracks are `show_in_detail: false` because the
 * printed table renders them there, and a summary is exactly what the detail
 * page does not need and the comparison does.
 */
export function compareTracks(family) {
  return family.attributes.filter((a) => a.datatype === 'structured' && a.kind !== 'reverse' && !a.isProse);
}

export default function GenericCompare({ segment }) {
  const family = getFamily(segment, getActiveSystem());
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const backTo = (typeof location.state?.from === 'string' && location.state.from)
    || `/${segment}`;

  const raw = searchParams.get('compare') || '';
  const { tokens, truncated } = useMemo(() => parseCompareParam(raw), [raw]);
  const tokenKey = tokens.map(tokenString).join(',');

  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const { tokens: wanted } = parseCompareParam(tokenKey);
    if (!wanted.length) {
      setColumns([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    Promise.all(wanted.map(async (t) => {
      try {
        const query = t.rulebook ? `?rulebook=${encodeURIComponent(t.rulebook)}` : '';
        const response = await apiClient.get(
          `/api/${segment}/${encodeURIComponent(t.slug)}${query}`
        );
        return { ...t, entry: response.data };
      } catch (err) {
        return { ...t, entry: null };
      }
    })).then((cols) => {
      if (cancelled) return;
      setColumns(cols);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [segment, tokenKey]);

  const { plural } = family.labels;
  const system = getActiveSystem();
  const spine = useMemo(() => compareSpine(family), [family]);
  const tracks = useMemo(() => compareTracks(family), [family]);

  const rewrite = (nextTokens) => {
    const next = new URLSearchParams(searchParams);
    if (nextTokens.length) next.set('compare', nextTokens.map(tokenString).join(','));
    else next.delete('compare');
    setSearchParams(next);
  };
  const pinPrinting = (slug, rulebookSlug) => {
    rewrite(tokens.map((t) => (t.slug === slug ? { slug, rulebook: rulebookSlug } : t)));
  };
  const removeColumn = (slug) => rewrite(tokens.filter((t) => t.slug !== slug));

  // A row earns its place when ANY compared entry answers it (the union rule
  // of issue 188); the all-empty rows disappear rather than padding the table.
  const rows = [
    ...spine.filter(
      (attr) => columns.some((c) => {
        const v = c.entry && c.entry[attr.key];
        return !(v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length));
      })
    ),
    // A track row earns its place when ANY compared entry's value reads as a
    // track (issue 628); the rest of the structured values stay out as before.
    ...tracks.filter((attr) => columns.some((c) => c.entry && summarizeTrack(c.entry[attr.key]) !== null)),
  ];

  const cellText = (attr, entry) => {
    const value = entry ? entry[attr.key] : undefined;
    if (value === undefined || value === null || value === ''
      || (Array.isArray(value) && !value.length)) return '—';
    if (attr.datatype === 'structured') return summarizeTrack(value) || '—';
    return attributeText(attr, value, { system }) || '—';
  };

  return (
    <>
      <Helmet>
        <title>Compare {plural} - The Dragon&apos;s Table</title>
        <meta name="description" content={`Compare ${plural.toLowerCase()} side by side.`} />
      </Helmet>

      <div className="space-y-6" data-testid="compare-view">
        <Link to={backTo} className="back-link">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to {plural}</span>
        </Link>

        <div className="page-header">
          <Columns3 className="page-icon" />
          <h1 className="page-title">Compare {plural}</h1>
        </div>

        {truncated && (
          <div className="card text-secondary" role="status">
            Comparing the first {MAX_COMPARE} — up to {MAX_COMPARE} {plural.toLowerCase()} can
            be compared at once.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64" role="status" aria-live="polite">
            <div className="loading-spinner h-10 w-10" role="status" aria-label="Loading"></div>
          </div>
        ) : !tokens.length ? (
          <div className="card text-center">
            <p className="no-results">Nothing to compare yet.</p>
            <Link to={`/${segment}`} className="btn btn-primary mt-4">
              Pick {plural.toLowerCase()} from the list
            </Link>
          </div>
        ) : (
          <ErrorBoundary>
            {tokens.length < 2 && (
              <div className="card text-secondary" role="status">
                Pick at least one more from the{' '}
                <Link to={`/${segment}`} className="class-level-link underline">
                  {plural.toLowerCase()} list
                </Link>{' '}
                to compare.
              </div>
            )}
            <div className="card">
              {/* Wide content scrolls in its own container; the page body
                  never scrolls horizontally. The label column stays visible
                  while the value columns scroll under it. */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="compare-table">
                  <thead>
                    <tr>
                      <th
                        className="table-header text-left align-bottom p-2 sticky left-0 z-10"
                        style={{ background: 'var(--bg-card, inherit)' }}
                        scope="col"
                      >
                        <span className="sr-only">Attribute</span>
                      </th>
                      {columns.map((col) => {
                        const entry = col.entry;
                        const name = entry
                          ? (entry[family.nameAttr.key] || entry.name || col.slug)
                          : col.slug;
                        const printings = entry?.printings || [];
                        const resolved = resolvedPrinting(printings, col.rulebook);
                        return (
                          <th
                            key={col.slug}
                            className="text-left align-top p-2 min-w-[10rem]"
                            scope="col"
                            data-testid={`compare-col-${col.slug}`}
                          >
                            <div className="flex items-start justify-between gap-1">
                              {entry ? (
                                <Link
                                  to={`/${segment}?slug=${encodeURIComponent(col.slug)}${
                                    col.rulebook ? `&rulebook=${encodeURIComponent(col.rulebook)}` : ''
                                  }`}
                                  state={{ from: `${location.pathname}${location.search}` }}
                                  className="spell-title text-base class-level-link"
                                >
                                  {name}
                                </Link>
                              ) : (
                                <span className="spell-title text-base">{name}</span>
                              )}
                              <button
                                type="button"
                                onClick={() => removeColumn(col.slug)}
                                className="flex-shrink-0 p-1"
                                aria-label={`Remove ${name} from comparison`}
                                title="Remove from comparison"
                              >
                                <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                              </button>
                            </div>
                            {!entry && (
                              <div className="error-message font-normal">Not found</div>
                            )}
                            {/* WHICH printing this column shows — always
                                labelled, because values differ between
                                printings and a silently mixed comparison is
                                worse than none. */}
                            {resolved && (
                              <div
                                className="font-normal text-muted"
                                data-testid={`compare-printing-${col.slug}`}
                              >
                                {resolved.rulebook_name}
                              </div>
                            )}
                            {printings.length > 1 && (
                              <div className="flex flex-wrap gap-1 mt-1 font-normal">
                                {printings.map((p) => (
                                  <button
                                    key={p.rulebook_slug}
                                    type="button"
                                    onClick={() => pinPrinting(col.slug, p.rulebook_slug)}
                                    className={`alphabetical-button ${
                                      resolved && resolved.rulebook_slug === p.rulebook_slug
                                        ? 'alphabetical-button-active'
                                        : 'alphabetical-button-inactive'
                                    }`}
                                  >
                                    {p.rulebook_name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((attr) => (
                      <tr key={attr.key} className="table-row">
                        <th
                          className="text-left align-top p-2 font-medium sticky left-0 z-10 whitespace-nowrap"
                          style={{ background: 'var(--bg-card, inherit)', color: 'var(--text-muted)' }}
                          scope="row"
                        >
                          {attr.label}
                        </th>
                        {columns.map((col) => (
                          <td
                            key={col.slug}
                            className="align-top p-2 min-w-[10rem] text-secondary"
                            data-testid={`compare-cell-${attr.key}-${col.slug}`}
                          >
                            {cellText(attr, col.entry)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ErrorBoundary>
        )}
      </div>
    </>
  );
}
